import { Link } from "@/lib/router-compat";
import { Helmet } from "react-helmet-async";
import SiteHeader from "@/components/SiteHeader";
import VideoBanner, { SidebarVideoBanners } from "@/components/blackbox/VideoBanner";
import { articles } from "@/data/articles";
import { getPostSlug } from "@/utils/slugUtils";
import { BookOpen, MessageSquare, AlertTriangle, CheckCircle, ExternalLink, HelpCircle } from "lucide-react";
import tysonGlandsDiagram from "@/assets/tyson-glands-diagram.png";
import fordyceSpotsDiagram from "@/assets/fordyce-spots-diagram.png";
import electrocauterizationImg from "@/assets/electrocauterization-treatment.png";
const PillarPage = () => {
  // Get posts by category for internal linking
  const pppPosts = articles.filter(p => p.category === "Penile Pimples" || p.category === "Pearly Papules").slice(0, 5);
  const tysonPosts = articles.filter(p => p.category === "Tyson's Glands").slice(0, 5);

  // MedicalWebPage Schema for better E-E-A-T
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "headline": "Bolinhas Brancas no P-nis: É Normal ou DST? Guia Clínico Completo",
    "description": "Bolinhas brancas no p-nis: PPP, glândulas sebáceas e manchas de Fordyce. Guia clínico para diferenciar de DSTs. 48% dos homens têm PPP.",
    "author": {
      "@type": "Organization",
      "name": "Equipe de Saúde Método P-nis Liso",
      "url": "https://smothmethod.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Método P-nis Liso",
      "logo": {
        "@type": "ImageObject",
        "url": "https://smothmethod.com/logo.jpg"
      }
    },
    "datePublished": "2024-01-15",
    "dateModified": "2025-01-04",
    "lastReviewed": "2025-01-04",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://smothmethod.com/guias/pequenas-bolinhas-no-penis"
    },
    "about": [
      {
        "@type": "MedicalCondition",
        "name": "Pápulas Perladas P-nilianas",
        "alternateName": ["PPP", "Hirsuties coronae glandis", "Papilomas hirsutoides"],
        "description": "Pequenas bolinhas em formato de cúpula que aparecem ao redor da coroa do p-nis. Variação anatômica normal presente em 14% a 48% dos homens.",
        "associatedAnatomy": {
          "@type": "AnatomicalStructure",
          "name": "Coroa do p-nis (borda da glande)"
        },
        "possibleTreatment": {
          "@type": "MedicalTherapy",
          "name": "Eletrocauterização",
          "description": "Método de remoção 100% eficaz usando corrente elétrica"
        },
        "riskFactor": "Mais comum em homens não circuncidados",
        "signOrSymptom": "Pequenas bolinhas uniformes em formato de cúpula dispostas em fileiras"
      },
      {
        "@type": "MedicalCondition",
        "name": "Glândulas de Tyson",
        "alternateName": "Tyson's glands",
        "description": "Glândulas sebáceas localizadas no frênulo do p-nis que produzem esmegma. Estruturas anatômicas normais.",
        "associatedAnatomy": {
          "@type": "AnatomicalStructure",
          "name": "Frênulo do p-nis"
        }
      },
      {
        "@type": "MedicalCondition",
        "name": "Manchas de Fordyce",
        "alternateName": "Grânulos de Fordyce",
        "description": "Glândulas sebáceas visíveis que aparecem como pequenas manchas claras ou amarelo-esbranquiçadas na haste. Presentes em até 80% dos adultos.",
        "associatedAnatomy": {
          "@type": "AnatomicalStructure",
          "name": "Haste do p-nis"
        },
        "signOrSymptom": "Pequenos pontos claros ou amarelados, frequentemente em grupos"
      }
    ],
    "keywords": "pápulas perladas p-nilianas, PPP, glândulas de Tyson, manchas de Fordyce, bolinhas no p-nis, bolinhas p-nilianas normais, PPP vs DST",
    "articleSection": "Educação em Saúde Masculina",
    "wordCount": 2500
  };

  // BreadcrumbList schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Início",
        "item": "https://smothmethod.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Guias",
        "item": "https://smothmethod.com/guias"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Pequenas Bolinhas no P-nis",
        "item": "https://smothmethod.com/guias/pequenas-bolinhas-no-penis"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-[#f6f7f9]">
      {/* Sidebar banners for desktop */}
      <SidebarVideoBanners />
      <Helmet>
        <title>Bolinhas Brancas no P-nis: É Normal ou DST? (Guia Clínico 2026)</title>
        <meta name="description" content="Bolinhas brancas no p-nis: PPP, glândulas sebáceas (Tyson) e manchas de Fordyce. Guia clínico para diferenciar de DSTs e saber quando se preocupar." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://smothmethod.com/guias/pequenas-bolinhas-no-penis" />
        <link rel="alternate" hrefLang="pt-br" href="https://smothmethod.com/guias/pequenas-bolinhas-no-penis" />
        <link rel="alternate" hrefLang="x-default" href="https://smothmethod.com/guias/pequenas-bolinhas-no-penis" />
        <meta property="og:locale" content="pt_BR" />
        
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Bolinhas Brancas no P-nis: É Normal ou DST?" />
        <meta property="og:description" content="PPP, glândulas sebáceas e manchas de Fordyce: guia clínico para diferenciar bolinhas brancas no p-nis de DSTs." />
        <meta property="og:url" content="https://smothmethod.com/guias/pequenas-bolinhas-no-penis" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Bolinhas Brancas no P-nis: Normal ou DST?" />
        <meta name="twitter:description" content="Guia clínico sobre bolinhas brancas no p-nis: PPP, glândulas sebáceas e manchas de Fordyce." />

        <script type="application/ld+json">
          {JSON.stringify(schemaMarkup)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <SiteHeader />
      
      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="mb-6 text-sm" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-gray-500" itemScope itemType="https://schema.org/BreadcrumbList">
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/" className="hover:text-[#4267B2]" itemProp="item">
                <span itemProp="name">Início</span>
              </Link>
              <meta itemProp="position" content="1" />
            </li>
            <li className="mx-1">/</li>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <span className="text-gray-700" itemProp="name">Guias</span>
              <meta itemProp="position" content="2" />
            </li>
            <li className="mx-1">/</li>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <span className="text-gray-700 font-medium" itemProp="name">Pequenas Bolinhas</span>
              <meta itemProp="position" content="3" />
            </li>
          </ol>
        </nav>

        {/* Hero Section */}
        <article className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <header className="p-6 md:p-8 bg-gradient-to-r from-[#4267B2]/5 to-[#4267B2]/10 border-b border-gray-100">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-5 h-5 text-[#4267B2]" />
              <span className="text-sm font-medium text-[#4267B2]">Guia Completo</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight mb-4">
              Bolinhas Brancas no P-nis: É Normal ou DST?
            </h1>
            <p className="text-gray-600">
              Se você notou bolinhas e está em pânico — respire. Veja como saber se as suas são anatomia normal ou algo que requer atenção.
            </p>
            <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
              <span>Última atualização: Dezembro de 2024</span>
              <span>•</span>
              <span>15 min de leitura</span>
            </div>
          </header>

          <div className="p-6 md:p-8 prose prose-lg max-w-none">
            {/* Table of Contents */}
            <nav className="bg-gray-50 rounded-lg p-4 mb-8 not-prose">
              <h2 className="font-semibold text-gray-900 mb-3">Sumário</h2>
              <ol className="space-y-2 text-sm">
                <li><a href="#introduction" className="text-[#4267B2] hover:underline">1. Introdução</a></li>
                <li><a href="#ppp" className="text-[#4267B2] hover:underline">2. Pápulas Perladas P-nilianas (PPP) / Glândulas de Tyson</a></li>
                <li><a href="#fordyce" className="text-[#4267B2] hover:underline">3. Manchas de Fordyce</a></li>
                <li><a href="#differences" className="text-[#4267B2] hover:underline">4. Como Diferenciar de DSTs</a></li>
                <li><a href="#when-worry" className="text-[#4267B2] hover:underline">5. Quando Procurar um Médico</a></li>
                <li><a href="#related" className="text-[#4267B2] hover:underline">6. Artigos Relacionados</a></li>
                <li><a href="#faq" className="text-[#4267B2] hover:underline">7. FAQ</a></li>
              </ol>
            </nav>

            {/* Introduction */}
            <section id="introduction">
              <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">1. Introdução</h2>
              <p className="text-gray-700 mb-4">
                Se você notou pequenas bolinhas no p-nis e entrou em pânico imediatamente, não está sozinho. Milhões de homens vivenciam exatamente esse medo todo ano, e a boa notícia é: <strong>na maioria dos casos, essas bolinhas são totalmente normais e inofensivas</strong>.
              </p>
              <p className="text-gray-700 mb-4">
                O problema não são as bolinhas em si — é a falta de informação. A educação sexual raramente aborda variações anatômicas normais, fazendo com que os homens assumam o pior quando notam algo diferente em seus corpos.
              </p>
              <p className="text-gray-700 mb-4">
                Este guia aborda os três tipos mais comuns de bolinhas p-nilianas benignas, explica como elas se diferem das infecções sexualmente transmissíveis e ajuda você a entender quando deve (e quando não deve) se preocupar.
              </p>
              <p className="text-gray-700 mb-4">
                Se as suas bolinhas estão especificamente na cabeça (glande) ou na coroa, veja também o guia dedicado:{" "}
                <Link to="/guias/bolhas-na-cabeca-do-penis" className="text-[#4267B2] hover:underline">bolhas e bolinha na cabeça do p-nis</Link>.
              </p>

            </section>

            {/* PPP / Tyson Glands Section */}
            <section id="ppp">
              <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">2. Pápulas Perladas P-nilianas (PPP) / Glândulas de Tyson</h2>
              <p className="text-gray-700 mb-4">
                As <strong>pápulas perladas p-nilianas (PPP)</strong>, também conhecidas como <strong>glândulas de Tyson</strong> ou papilomas hirsutoides, são pequenas bolinhas em formato de cúpula que aparecem em uma ou mais fileiras ao redor da coroa (borda) da glande do p-nis. São o tipo mais comum de bolinha p-niliana que causa preocupação — e também o mais incompreendido. Essas glândulas produzem esmegma, um lubrificante natural.
              </p>

              <figure className="my-6 not-prose">
                <img 
                  src={tysonGlandsDiagram} 
                  alt="Diagrama de pápulas perladas p-nilianas (PPP) e glândulas de Tyson mostrando pequenas bolinhas brancas na glande do p-nis ao redor da coroa — variação anatômica normal" 
                  className="w-full max-w-md mx-auto rounded-lg border border-gray-200"
                />
                <figcaption className="text-center text-sm text-gray-500 mt-2">
                  Diagrama anatômico mostrando a localização das PPP / glândulas de Tyson
                </figcaption>
              </figure>
              
              <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">Principais Características:</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                <li>Pequenas (1–2 mm), uniformes, em formato de cúpula</li>
                <li>Cor da pele, brancas ou levemente rosadas</li>
                <li>Dispostas em fileiras organizadas ao redor da coroa</li>
                <li>Textura lisa (não rugosa)</li>
                <li>Presentes desde a puberdade — não surgem de repente</li>
                <li>Não crescem, não se espalham nem mudam com o tempo</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">Quão Comuns São?</h3>
              <p className="text-gray-700 mb-4">
                Estudos mostram que <strong>14% a 48% dos homens têm PPP</strong>. Isso é potencialmente quase metade de todos os homens. São mais comuns em homens não circuncidados, mas também ocorrem em circuncidados.
              </p>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 my-6 not-prose">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-800">PPP NÃO é:</p>
                    <ul className="text-green-700 text-sm mt-1 space-y-1">
                      <li>• Uma DST ou infecção</li>
                      <li>• Contagioso</li>
                      <li>• Causado por má higiene</li>
                      <li>• Algo que precise de tratamento</li>
                    </ul>
                  </div>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">Opções de Tratamento</h3>
              <p className="text-gray-700 mb-4">
                Embora as PPP sejam inofensivas e não exijam tratamento, muitos homens optam por removê-las por razões estéticas. <strong>A eletrocauterização é considerada o método de tratamento mais eficaz</strong>, com taxa de sucesso de 100% quando realizada por um profissional qualificado.
              </p>

              <figure className="my-6 not-prose">
                <img 
                  src={electrocauterizationImg} 
                  alt="Caneta de eletrocauterização para remoção de PPP — método 100% eficaz para remoção de pápulas perladas p-nilianas e glândulas de Tyson" 
                  className="w-full max-w-md mx-auto rounded-lg border border-gray-200"
                />
                <figcaption className="text-center text-sm text-gray-500 mt-2">
                  Eletrocauterização — o método mais eficaz para remoção das PPP
                </figcaption>
              </figure>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-6 not-prose">
                <p className="font-medium text-blue-800 mb-2">Por que Eletrocauterização?</p>
                <ul className="text-blue-700 text-sm space-y-1">
                  <li>• <strong>100% eficaz</strong> — remoção completa em uma única sessão</li>
                  <li>• Cicatrizes mínimas quando feita corretamente</li>
                  <li>• Procedimento rápido com anestesia local</li>
                  <li>• Resultados permanentes — as pápulas não voltam</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-4 my-6 not-prose">
                <p className="text-sm font-medium text-gray-700 mb-2">Artigos relacionados:</p>
                <ul className="space-y-1">
                  {pppPosts.slice(0, 3).map(post => (
                    <li key={post.id}>
                      <Link 
                        to={`/artigos/${getPostSlug(post)}`}
                        className="text-sm text-[#4267B2] hover:underline flex items-center gap-1"
                      >
                        <MessageSquare className="w-3 h-3" />
                        {post.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* Fordyce Section */}
            <section id="fordyce">
              <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">3. Manchas de Fordyce</h2>
              <p className="text-gray-700 mb-4">
                As <strong>manchas de Fordyce</strong> são pequenas bolinhas claras ou amarelo-esbranquiçadas que podem aparecer na haste do p-nis, no escroto e até nos lábios. São, essencialmente, glândulas sebáceas (de óleo) visíveis.
              </p>

              <figure className="my-6 not-prose">
                <img 
                  src={fordyceSpotsDiagram} 
                  alt="Manchas de Fordyce na haste do p-nis — pequenas bolinhas claras amareladas, variação anatômica normal, não DST" 
                  className="w-full max-w-md mx-auto rounded-lg border border-gray-200"
                />
                <figcaption className="text-center text-sm text-gray-500 mt-2">
                  Exemplo de manchas de Fordyce na pele do p-nis
                </figcaption>
              </figure>
              
              <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">Principais Características:</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                <li>Pontinhos (1–3 mm) claros ou amarelados</li>
                <li>Frequentemente aparecem em grupos</li>
                <li>Localizados na haste, não na glande</li>
                <li>Podem ficar mais visíveis quando a pele é esticada</li>
                <li>Presentes desde o nascimento, ficam mais visíveis na puberdade</li>
              </ul>

              <p className="text-gray-700 mb-4">
                <strong>Até 80% dos adultos têm manchas de Fordyce em algum lugar do corpo.</strong> São tão comuns que são consideradas uma variação anatômica normal, não uma doença.
              </p>
            </section>


            {/* Differences from STDs */}
            <section id="differences">
              <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">4. Como Diferenciar de DSTs</h2>
              <p className="text-gray-700 mb-4">
                Essa é a pergunta que todo mundo quer ver respondida. Veja como as bolinhas anatômicas normais se diferem de sintomas preocupantes:
              </p>


              <div className="overflow-x-auto my-6 not-prose">
                <table className="w-full text-sm border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900">Característica</th>
                      <th className="px-4 py-3 text-left font-semibold text-green-700">Normal (PPP/Fordyce/Tyson)</th>
                      <th className="px-4 py-3 text-left font-semibold text-red-700">Preocupante (Possível DST)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 font-medium">Surgimento</td>
                      <td className="px-4 py-3 text-green-700">Desde a puberdade</td>
                      <td className="px-4 py-3 text-red-700">Surgiu recentemente após contato sexual</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium">Textura</td>
                      <td className="px-4 py-3 text-green-700">Lisa, uniforme</td>
                      <td className="px-4 py-3 text-red-700">Rugosa, semelhante a couve-flor (verrugas)</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium">Padrão</td>
                      <td className="px-4 py-3 text-green-700">Organizado em fileiras, simétrico</td>
                      <td className="px-4 py-3 text-red-700">Distribuição aleatória</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium">Sintomas</td>
                      <td className="px-4 py-3 text-green-700">Nenhum — sem dor, coceira ou secreção</td>
                      <td className="px-4 py-3 text-red-700">Dor, coceira, ardência, secreção</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium">Mudanças com o tempo</td>
                      <td className="px-4 py-3 text-green-700">Estáveis — não crescem nem se espalham</td>
                      <td className="px-4 py-3 text-red-700">Podem crescer, multiplicar ou mudar</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">Perguntas Rápidas para Autoavaliação:</h3>
              <ol className="list-decimal pl-6 text-gray-700 mb-4 space-y-2">
                <li>Essas bolinhas estão presentes desde a puberdade ou antes de você ser sexualmente ativo? Se sim, não podem ser DST.</li>
                <li>Elas causam dor, coceira, ardência ou secreção? Anatomia normal não causa sintomas.</li>
                <li>Mudaram, cresceram ou se espalharam com o tempo? Bolinhas normais são estáveis.</li>
                <li>São lisas e uniformes ou rugosas e irregulares? Lisas geralmente indicam normalidade.</li>
              </ol>
            </section>

            {/* When to Worry */}
            <section id="when-worry">
              <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">5. Quando Procurar um Médico</h2>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-6 not-prose">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-800">Procure um médico se você apresentar:</p>
                    <ul className="text-red-700 text-sm mt-2 space-y-1">
                      <li>• Bolhas dolorosas que aparecem e somem (possível herpes)</li>
                      <li>• Crescimentos rugosos com aspecto de couve-flor (possíveis verrugas)</li>
                      <li>• Bolinhas que surgiram após novo contato sexual</li>
                      <li>• Qualquer secreção, ardência ou coceira persistente</li>
                      <li>• Bolinhas que estão crescendo ou se multiplicando</li>
                      <li>• Uma única ferida indolor (possível sífilis)</li>
                    </ul>
                  </div>
                </div>
              </div>


              <p className="text-gray-700 mb-4">
                Mesmo que você tenha certeza de que suas bolinhas são normais, não há nada de errado em buscar uma opinião profissional. Um dermatologista geralmente consegue identificar PPP, manchas de Fordyce ou glândulas de Tyson em segundos, apenas com um exame visual.
              </p>

              <p className="text-gray-700 mb-4">
                <strong>A tranquilidade de um diagnóstico profissional geralmente vale o breve constrangimento da consulta.</strong>
              </p>
            </section>

            {/* Related Articles Section */}
            <section id="related">
              <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4">6. Artigos Relacionados</h2>
              <p className="text-gray-700 mb-4">
                Explore nossos artigos educativos sobre dúvidas comuns a respeito de bolinhas no p-nis, PPP e glândulas de Tyson.
              </p>

              <div className="bg-white border border-gray-200 rounded-lg p-4 my-6 not-prose">
                <p className="font-medium text-gray-900 mb-3">Artigos em Destaque:</p>
                <div className="space-y-3">
                  {articles.slice(0, 6).map(post => (
                    <Link 
                      key={post.id}
                      to={`/artigos/${getPostSlug(post)}`}
                      className="block p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <span className="text-xs font-medium text-[#4267B2] bg-[#4267B2]/10 px-2 py-0.5 rounded">
                        {post.category}
                      </span>
                      <h4 className="font-medium text-gray-900 mt-1 text-sm">
                        {post.title}
                      </h4>
                    </Link>
                  ))}
                </div>
                <Link 
                  to="/artigos" 
                  className="inline-flex items-center mt-4 text-[#4267B2] font-medium text-sm hover:underline"
                >
                  Ver todos os {articles.length} artigos →
                </Link>
              </div>
            </section>

            {/* Conclusion */}
            <section className="mt-8 p-6 bg-[#4267B2]/5 rounded-lg not-prose">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Pontos-Chave</h2>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                  <span>Bolinhas pequenas e indolores presentes desde a puberdade quase sempre são anatomia normal.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                  <span>A PPP afeta até 48% dos homens. As manchas de Fordyce afetam até 80%. Você não está sozinho.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                  <span>Bolinhas normais não causam sintomas — sem dor, coceira ou secreção.</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                  <span>Em caso de dúvida, procure um dermatologista. O diagnóstico é rápido e traz tranquilidade.</span>
                </li>
              </ul>
            </section>
          </div>
        </article>

        {/* Footer */}
        <footer className="text-center py-6 mt-8 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Este guia é apenas para fins educativos e não substitui aconselhamento médico profissional. Sempre consulte um profissional de saúde para questões pessoais de saúde.
          </p>
        </footer>

        {/* Footer Video Banner - last element */}
        <VideoBanner />
      </main>
    </div>
  );
};

export default PillarPage;
